<script setup>
// The three join choices (JOIN_SETTINGS_PLAN.md §7.3) plus the ladder placement
// switch. Each choice is a select rather than a segmented control: the options
// per choice differ (two here, three there), so segments never lined up down the
// panel, and three solid pills stacked read far heavier than the decision merits.
// Emits a PATCH; the host saves.
const props = defineProps({
  settings: {type: Object, required: true},   // JoinSettingsResponse
  saving: {type: Boolean, default: false},
});
const emit = defineEmits(['change']);

const link = computed(() => props.settings?.link ?? {});
const enabled = computed(() => props.settings?.enabled !== false);
const isClub = computed(() => props.settings?.referenceType === 0);
const entity = computed(() => props.settings?.entityTypeName ?? 'competition');

// Switching joining off deletes the link, so it asks first. The confirm is
// inline rather than a dialog: this panel is already a dialog, and a second
// overlay on top of a select's menu fought the overlay stack and dismissed
// both. Inline also keeps the question next to the control that raised it.
const confirmingOff = ref(false);
const SCOPE_OFF = 'off';

const scopeValue = computed(() =>
    enabled.value ? (isClub.value ? 0 : link.value.inviteScope ?? 0) : SCOPE_OFF);
const scopeItems = computed(() => [
  {value: 0, title: 'Anyone with the link', icon: 'mdi-earth'},
  ...(isClub.value ? [] : [{value: 1, title: 'Club players only', icon: 'mdi-account-group'}]),
  {value: SCOPE_OFF, title: 'Nobody, joining is off', icon: 'mdi-cancel'},
]);

const joinItems = [
  {value: 0, title: 'They join straight away', icon: 'mdi-flash'},
  {value: 1, title: 'You approve each request', icon: 'mdi-account-check'},
];

// A club's members are already in, so "members only" means nothing there.
const pageItems = computed(() => [
  {value: 1, title: 'Hidden, link only', icon: 'mdi-eye-off-outline'},
  ...(isClub.value ? [] : [{value: 2, title: 'Shown to club players', icon: 'mdi-account-eye-outline'}]),
  {value: 0, title: 'Shown to everyone', icon: 'mdi-eye-outline'},
]);

// A club created with the Join button switch off gets the new-link default, 2
// (InviteEntityPageSetting.VisibleMembersOnly). The club has no option for it, so the
// select showed a bare "2". For a club it works as link only (the link is taken off
// the page for non-members, and members are already in), so it reads as that.
const pageValue = computed(() => {
  const v = link.value.inviteEntityPageSetting ?? 2;
  return isClub.value && v === 2 ? 1 : v;
});

const set = (patch) => {
  if (props.saving) return;
  emit('change', patch);
};

// The question belongs to one entity and one moment: a different entity, or a
// different answer, withdraws it.
watch(() => props.settings?.referenceId, () => { confirmingOff.value = false; });

const onScope = (value) => {
  if (value === SCOPE_OFF) {
    confirmingOff.value = true;
    return;
  }
  confirmingOff.value = false;
  // Choosing a scope while off switches joining back on with a fresh link.
  set(enabled.value ? {inviteScope: value} : {enabled: true, inviteScope: value});
};

const confirmOff = () => {
  // Keep the question up if a save is already running: set() would ignore the
  // request, and hiding the box would look like it had been accepted.
  if (props.saving) return;
  confirmingOff.value = false;
  set({enabled: false});
};

const pageHint = computed(() => {
  if (pageValue.value === 1) return 'Only people you send the link to can join.';
  if (link.value.inviteEntityPageSetting === 2) return `Club players see a Join button; everyone else needs the link.`;
  return `Anyone viewing the ${entity.value} page sees a Join button.`;
});
</script>

<template>
  <div class="jsc">
    <div class="jsc-row">
      <div class="tl-label">Who can join</div>
      <v-select
          :model-value="scopeValue"
          :items="scopeItems"
          item-title="title"
          item-value="value"
          density="compact"
          variant="outlined"
          hide-details
          :disabled="saving"
          @update:model-value="onScope">
        <template #selection="{item}">
          <v-icon size="15" class="jsc-ic" :icon="item.raw.icon"/>{{ item.title }}
        </template>
        <template #item="{props: itemProps, item}">
          <v-list-item v-bind="itemProps" :prepend-icon="item.raw.icon"/>
        </template>
      </v-select>
      <div v-if="confirmingOff" class="jsc-confirm">
        <div>
          The invite link will stop working and nobody can join until you switch it back on.
          Switching it on again creates a new link address.
        </div>
        <div class="jsc-confirm-actions">
          <v-btn variant="text" size="small" :disabled="saving" @click="confirmingOff = false">Keep joining on</v-btn>
          <v-btn color="primary" variant="flat" size="small" rounded="pill" :disabled="saving" @click="confirmOff">Switch off</v-btn>
        </div>
      </div>
      <div v-else-if="!enabled" class="jsc-hint">
        There is no invite link and the {{ entity }} page shows no Join button. Choose who can join to switch it back on.
      </div>
    </div>

    <template v-if="enabled">
      <div class="jsc-row">
        <div class="tl-label">How they join</div>
        <v-select
            :model-value="link.inviteJoinSetting ?? 0"
            :items="joinItems"
            item-title="title"
            item-value="value"
            density="compact"
            variant="outlined"
            hide-details
            :disabled="saving"
            @update:model-value="(v) => set({inviteJoinSetting: v})">
          <template #selection="{item}">
            <v-icon size="15" class="jsc-ic" :icon="item.raw.icon"/>{{ item.title }}
          </template>
          <template #item="{props: itemProps, item}">
            <v-list-item v-bind="itemProps" :prepend-icon="item.raw.icon"/>
          </template>
        </v-select>
      </div>

      <div class="jsc-row">
        <div class="tl-label">Join button on the {{ entity }} page</div>
        <v-select
            :model-value="pageValue"
            :items="pageItems"
            item-title="title"
            item-value="value"
            density="compact"
            variant="outlined"
            hide-details
            :disabled="saving"
            @update:model-value="(v) => set({inviteEntityPageSetting: v})">
          <template #selection="{item}">
            <v-icon size="15" class="jsc-ic" :icon="item.raw.icon"/>{{ item.title }}
          </template>
          <template #item="{props: itemProps, item}">
            <v-list-item v-bind="itemProps" :prepend-icon="item.raw.icon"/>
          </template>
        </v-select>
        <div class="jsc-hint">{{ pageHint }}</div>
      </div>

      <!-- Placement: ladders carry the switch; cycles always hold -->
      <div v-if="settings.placementDescription" class="jsc-row">
        <label v-if="settings.holdForPlacementEditable" class="jsc-switch">
          <div class="flex-fill min-w-0">
            <div class="jsc-switch-t">Hold new players until you place them</div>
            <div class="jsc-switch-s">
              {{ settings.holdForPlacement ? settings.placementDescription : settings.autoPlacementDescription }}
            </div>
          </div>
          <v-switch :model-value="settings.holdForPlacement" color="primary" hide-details inset density="compact"
                    :disabled="saving" @update:model-value="(v) => set({holdForPlacement: !!v})"/>
        </label>
        <div v-else class="jsc-note">
          <v-icon size="15" color="primary" class="mr-2">mdi-account-clock-outline</v-icon>
          <span>{{ settings.placementDescription }}</span>
        </div>
      </div>
    </template>

  </div>
</template>

<style scoped lang="scss">
.jsc {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.jsc-confirm {
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgb(var(--v-theme-warningTint));
  border: 1px solid rgb(var(--v-theme-warning));
  font-size: 12.5px;
  line-height: 1.45;
  color: rgb(var(--v-theme-text));
}

.jsc-confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.jsc-ic {
  margin-right: 8px;
  color: rgb(var(--v-theme-textSubtle));
}

.jsc-hint {
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 5px;
  line-height: 1.4;
}

.jsc-switch {
  display: flex;
  align-items: center;
  gap: 12px;

  /* An inset v-switch sizes itself from its track and would push past the
     card's right edge on a phone unless it is told not to grow or shrink. */
  :deep(.v-switch) {
    flex: 0 0 auto;
  }

  padding: 10px 12px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 10px;
  cursor: pointer;
}

.jsc-switch-t {
  font-size: 13px;
  font-weight: 700;
}

.jsc-switch-s {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 1px;
  line-height: 1.4;
}

.jsc-note {
  display: flex;
  align-items: flex-start;
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
  line-height: 1.45;
}
</style>
